import { useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Ban, Check, ExternalLink, FileCheck2, History, Paperclip, Pencil, Plus, Printer, RotateCcw, Send, Trash2, Truck, Undo2, Upload, X } from "lucide-react";
import { clsx } from "clsx";
import DOMPurify from "dompurify";
import { Spinner } from "@/components/ui/Spinner";
import { Button } from "@/components/ui/Button";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { FilterMultiSelect } from "@/components/ui/FilterMultiSelect";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import { useAuthStore } from "@/store/auth";
import {
  AttachmentLinks,
  DispatchBadge,
  PageHeader,
  TableShell,
  Tabs,
  Th,
} from "@/features/power_projects/components/ui";
import { fmtDate, fmtDateTime } from "@/features/power_projects/utils";
import { useCorrespondenceRegister } from "@/features/power_projects/hooks";
import type { CorrespondenceRegister } from "@/features/power_projects/types";
import {
  DISPATCHED_REGISTER_STATUSES,
  PrintOrderTrackingNumber,
  RegisterTrackingNumber,
  TrackingNumberText,
} from "../components/TrackingNumber";
import { CancelDispatchButton } from "@/features/paper_send/components/CancelDispatchButton";
import { uploadPrintOrderDescriptionImage } from "../api";
import { usePrintOrders, usePrintOrderMutations } from "../hooks";
import type { PrintOrder, PrintOrderStatus } from "../types";

// Description is authored in a rich-text editor (stored as HTML). Render it sanitized;
// fall back to plain text for any legacy plain-string descriptions.
function isHtmlContent(content: string) {
  return content.trimStart().startsWith("<");
}

function sanitize(html: string) {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "ul", "ol", "li", "img", "h1", "h2", "h3"],
    ALLOWED_ATTR: ["src", "alt", "width", "height", "start"],
  });
}

function DescriptionBody({ html }: { html: string }) {
  if (!html) return null;
  if (isHtmlContent(html)) {
    return (
      <div
        className="rich-text-content text-[11px] text-[var(--color-text-secondary)] mt-0.5"
        dangerouslySetInnerHTML={{ __html: sanitize(html) }}
      />
    );
  }
  return <div className="text-[11px] text-[var(--color-text-secondary)]">{html}</div>;
}

// ── status badge ──────────────────────────────────────────────────────────────
const STATUS_STYLE: Record<PrintOrderStatus, string> = {
  pending: "bg-amber-100 text-amber-700 border-amber-300",
  accepted: "bg-blue-100 text-blue-700 border-blue-300",
  printed: "bg-emerald-100 text-emerald-700 border-emerald-300",
  sent: "bg-teal-100 text-teal-700 border-teal-300",
  returned: "bg-orange-100 text-orange-700 border-orange-300",
  rejected: "bg-[var(--color-primary-dim)] text-[var(--color-primary)] border-[var(--color-primary)]",
  cancelled: "bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)] border-[var(--color-border)]",
};

function StatusBadge({ order }: { order: PrintOrder }) {
  return (
    <span
      className={clsx(
        "inline-block border px-1.5 py-0.5 text-[10px] font-bold font-mono uppercase tracking-wider",
        STATUS_STYLE[order.status],
      )}
    >
      {order.status_display}
    </span>
  );
}

// ── shared order form (create + edit) ─────────────────────────────────────────
// On create the file is required; on edit it is optional — omitting it keeps the current file,
// choosing one replaces it. Name / description / shipping are always editable.
function PrintOrderForm({
  title,
  requireFile,
  existingFileName,
  initialName = "",
  initialDescription = "",
  initialNeedsShipping = false,
  submitLabel,
  pending,
  onSubmit,
  onCancel,
}: {
  title: string;
  requireFile: boolean;
  existingFileName?: string;
  initialName?: string;
  initialDescription?: string;
  initialNeedsShipping?: boolean;
  submitLabel: string;
  pending: boolean;
  onSubmit: (v: { name: string; description: string; needsShipping: boolean; file: File | null }) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [needsShipping, setNeedsShipping] = useState(initialNeedsShipping);
  const [file, setFile] = useState<File | null>(null);

  const canSubmit = name.trim().length > 0 && (!requireFile || !!file);

  return (
    <div className="card p-3 mb-4 space-y-3">
      <h3 className="section-label">{title}</h3>
      <label className="block sm:max-w-md">
        <span className="section-label">Nazwa</span>
        <input className="input-base mt-1" placeholder="Nazwa zlecenia" value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      <div className="block">
        <span className="section-label">Opis</span>
        <div className="mt-1">
          <RichTextEditor
            value={description}
            onChange={setDescription}
            placeholder="Opis / uwagi do wydruku…"
            onImageUpload={uploadPrintOrderDescriptionImage}
          />
        </div>
      </div>
      <div className="space-y-1 sm:max-w-md">
        <span className="section-label">Plik (dokument lub ZIP)</span>
        {existingFileName && !file && (
          <div className="flex items-center gap-1.5 text-[11px] font-mono text-[var(--color-text-secondary)]">
            <Paperclip size={11} className="shrink-0 text-[var(--color-text-muted)]" />
            <span className="truncate max-w-[240px]" title={existingFileName}>{existingFileName}</span>
            <span className="text-[var(--color-text-muted)]">— przeciągnij nowy plik, aby podmienić</span>
          </div>
        )}
        <FileDropZone
          files={file ? [file] : []}
          onFilesChange={(fs) => setFile(fs[0] ?? null)}
          className="mt-1"
        />
      </div>
      <label className="inline-flex items-center gap-2 cursor-pointer select-none">
        <input type="checkbox" className="h-4 w-4" checked={needsShipping} onChange={(e) => setNeedsShipping(e.target.checked)} />
        <span className="section-label">Potrzebna wysyłka</span>
      </label>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          disabled={!canSubmit}
          loading={pending}
          onClick={() => canSubmit && onSubmit({ name: name.trim(), description, needsShipping, file })}
        >
          {submitLabel}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>Anuluj</Button>
        {!canSubmit && (
          <span className="section-label text-[var(--color-primary)]">
            {requireFile ? "Podaj nazwę i wybierz plik" : "Podaj nazwę"}
          </span>
        )}
      </div>
    </div>
  );
}

// ── create form ───────────────────────────────────────────────────────────────
function CreateForm({ onDone }: { onDone: () => void }) {
  const m = usePrintOrderMutations();
  return (
    <PrintOrderForm
      title="Nowe zlecenie wydruku"
      requireFile
      submitLabel="Utwórz zlecenie"
      pending={m.create.isPending}
      onSubmit={({ name, description, needsShipping, file }) => {
        if (!file) return;
        const fd = new FormData();
        fd.append("name", name);
        if (description) fd.append("description", description);
        fd.append("needs_shipping", needsShipping ? "true" : "false");
        fd.append("file", file);
        m.create.mutate(fd, { onSuccess: onDone });
      }}
      onCancel={onDone}
    />
  );
}

// ── edit form ─────────────────────────────────────────────────────────────────
// Editing is only offered while the order is still pending (see PrintOrderRow.canEdit).
function EditForm({ order, onDone }: { order: PrintOrder; onDone: () => void }) {
  const m = usePrintOrderMutations();
  return (
    <PrintOrderForm
      title={`Edycja zlecenia #${order.number}`}
      requireFile={false}
      existingFileName={order.file?.original_filename}
      initialName={order.name}
      initialDescription={order.description}
      initialNeedsShipping={order.needs_shipping}
      submitLabel="Zapisz zmiany"
      pending={m.update.isPending}
      onSubmit={({ name, description, needsShipping, file }) => {
        const fd = new FormData();
        fd.append("name", name);
        fd.append("description", description);
        fd.append("needs_shipping", needsShipping ? "true" : "false");
        if (file) fd.append("file", file);
        m.update.mutate({ id: order.id, fd }, { onSuccess: onDone });
      }}
      onCancel={onDone}
    />
  );
}

// ── delete (with inline confirm) ──────────────────────────────────────────────
function DeleteButton({ order }: { order: PrintOrder }) {
  const m = usePrintOrderMutations();
  const [confirming, setConfirming] = useState(false);
  if (!confirming) {
    return (
      <Button size="sm" variant="danger" onClick={() => setConfirming(true)}>
        <Trash2 size={13} />
        Usuń
      </Button>
    );
  }
  return (
    <div className="flex items-center gap-1.5">
      <span className="section-label">Na pewno?</span>
      <Button size="sm" variant="danger" loading={m.remove.isPending} onClick={() => m.remove.mutate(order.id)}>
        Tak, usuń
      </Button>
      <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>Nie</Button>
    </div>
  );
}

// ── reject (with reason) ───────────────────────────────────────────────────────
function RejectButton({ order }: { order: PrintOrder }) {
  const m = usePrintOrderMutations();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  if (!open) {
    return (
      <Button size="sm" variant="danger" onClick={() => setOpen(true)}>
        <X size={13} />
        Odrzuć
      </Button>
    );
  }
  return (
    <div className="w-[220px] border border-[var(--color-border)] border-l-[3px] border-l-[var(--color-primary)] bg-[var(--color-bg-surface)] p-2 space-y-2">
      <label className="block space-y-1">
        <span className="section-label">Powód odrzucenia</span>
        <input className="input-base text-xs py-1.5" placeholder="np. zły plik" value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
      <div className="flex gap-1.5">
        <Button size="sm" variant="danger" loading={m.reject.isPending} onClick={() => m.reject.mutate({ id: order.id, note }, { onSuccess: () => setOpen(false) })}>
          <X size={13} />
          Odrzuć
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Anuluj</Button>
      </div>
    </div>
  );
}

// ── proof of delivery (potwierdzenie odbioru) ──────────────────────────────────
// Shown for shipped orders (status=sent, needs_shipping). The dział realizacji (canRealize) may
// attach PDF proofs of delivery; everyone else just sees the link. Pod jednym zleceniem bywają
// listy do kilku osób i wraca kilka zwrotek: można je wskazać naraz albo dokładać pojedynczo
// („Dodaj zwrotkę") — backend scala je w jeden dokument. „Zmień" podmienia całość.
function ReceiptConfirmation({ order, canRealize }: { order: PrintOrder; canRealize: boolean }) {
  const m = usePrintOrderMutations();
  const inputRef = useRef<HTMLInputElement>(null);
  const modeRef = useRef<"append" | "replace">("append");
  const receipt = order.receipt_confirmation_file;

  const pick = (mode: "append" | "replace") => {
    modeRef.current = mode;
    inputRef.current?.click();
  };

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = ""; // allow re-picking the same filename after a failed/second upload
    if (files.length) m.uploadReceipt.mutate({ id: order.id, files, mode: modeRef.current });
  };

  return (
    <>
      {receipt && <AttachmentLinks files={[receipt]} />}
      {canRealize && (
        <>
          <input ref={inputRef} type="file" multiple accept="application/pdf,.pdf" className="hidden" onChange={onPick} />
          <Button
            size="sm"
            variant="ghost"
            loading={m.uploadReceipt.isPending && modeRef.current === "append"}
            onClick={() => pick("append")}
          >
            {receipt ? <Plus size={13} /> : <FileCheck2 size={13} />}
            {receipt ? "Dodaj zwrotkę" : "Potwierdzenie odbioru"}
          </Button>
          {receipt && (
            <Button
              size="sm"
              variant="ghost"
              loading={m.uploadReceipt.isPending && modeRef.current === "replace"}
              onClick={() => pick("replace")}
            >
              <Upload size={13} />
              Zmień
            </Button>
          )}
        </>
      )}
    </>
  );
}

// ── zwrot (returned shipment) ──────────────────────────────────────────────────
// Marking a shipment as returned is offered on shipped orders (status=sent, needs_shipping) to the
// same audience that marks them sent (canRealize). The scan of the return is optional: it may be
// attached right away, or later while the order stays in "zwrot".
function MarkReturnedButton({ order }: { order: PrintOrder }) {
  const m = usePrintOrderMutations();
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  if (!open) {
    return (
      <Button size="sm" variant="ghost" onClick={() => setOpen(true)}>
        <Undo2 size={13} />
        Zwrot
      </Button>
    );
  }
  return (
    <div className="w-[220px] border border-[var(--color-border)] border-l-[3px] border-l-orange-400 bg-[var(--color-bg-surface)] p-2 space-y-2">
      <div className="section-label">Oznacz jako zwrot</div>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="application/pdf,.pdf"
        className="hidden"
        onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
      />
      <div className="flex items-center gap-1.5 text-[11px] font-mono text-[var(--color-text-secondary)]">
        <Paperclip size={11} className="shrink-0 text-[var(--color-text-muted)]" />
        {files.length ? (
          // Kilka PDF-ów scala się na backendzie w jeden załącznik zwrotu.
          <span className="truncate max-w-[150px]" title={files.map((f) => f.name).join(", ")}>
            {files.length === 1 ? files[0].name : `${files.length} plików`}
          </span>
        ) : (
          <button type="button" className="underline" onClick={() => inputRef.current?.click()}>
            Załączniki (PDF, opcjonalnie)
          </button>
        )}
      </div>
      <div className="flex gap-1.5">
        <Button
          size="sm"
          variant="secondary"
          loading={m.markReturned.isPending}
          onClick={() => m.markReturned.mutate({ id: order.id, files }, { onSuccess: () => setOpen(false) })}
        >
          <Undo2 size={13} />
          Zwrot
        </Button>
        <Button size="sm" variant="ghost" onClick={() => { setFiles([]); setOpen(false); }}>Anuluj</Button>
      </div>
    </div>
  );
}

// Returned orders: everyone sees the scan (when there is one); the dział realizacji may still
// attach or replace it.
function ReturnAttachment({ order, canRealize }: { order: PrintOrder; canRealize: boolean }) {
  const m = usePrintOrderMutations();
  const inputRef = useRef<HTMLInputElement>(null);
  const modeRef = useRef<"append" | "replace">("append");
  const attachment = order.return_file;

  const pick = (mode: "append" | "replace") => {
    modeRef.current = mode;
    inputRef.current?.click();
  };

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = ""; // allow re-picking the same filename after a failed/second upload
    if (files.length) m.uploadReturnAttachment.mutate({ id: order.id, files, mode: modeRef.current });
  };

  return (
    <>
      {attachment && <AttachmentLinks files={[attachment]} />}
      {canRealize && (
        <>
          <input ref={inputRef} type="file" multiple accept="application/pdf,.pdf" className="hidden" onChange={onPick} />
          <Button
            size="sm"
            variant="ghost"
            loading={m.uploadReturnAttachment.isPending && modeRef.current === "append"}
            onClick={() => pick("append")}
          >
            {attachment ? <Plus size={13} /> : <Paperclip size={13} />}
            {attachment ? "Dodaj załącznik zwrotu" : "Załącznik zwrotu"}
          </Button>
          {attachment && (
            <Button
              size="sm"
              variant="ghost"
              loading={m.uploadReturnAttachment.isPending && modeRef.current === "replace"}
              onClick={() => pick("replace")}
            >
              <Upload size={13} />
              Zmień
            </Button>
          )}
        </>
      )}
    </>
  );
}

// ── one row ────────────────────────────────────────────────────────────────────
function PrintOrderRow({ order, canApprove, canRealize, isManager, myId }: {
  order: PrintOrder;
  canApprove: boolean;
  canRealize: boolean;
  isManager: boolean;
  myId?: string;
}) {
  const m = usePrintOrderMutations();
  const [showHistory, setShowHistory] = useState(false);
  const [editing, setEditing] = useState(false);
  const isAuthor = !!myId && order.created_by === myId;
  // Edit only while pending (author, technical director, or CEO).
  const canEdit = order.status === "pending" && (isAuthor || isManager);
  // Wycofanie własnej korespondencji z kolejki wydruku — autor, dyrektor techniczny lub prezes.
  // Usunąć da się wszystko, czego jeszcze nie wydrukowano/nie wysłano (także po akceptacji);
  // omyłkowe „Wydrukowane" najpierw cofa się przyciskiem obok.
  const canWithdraw = isAuthor || isManager;
  const canDelete =
    canWithdraw && order.status !== "printed" && order.status !== "sent" && order.status !== "returned";
  const canUndoPrint = canWithdraw && order.status === "printed";
  // Po akceptacji edycja jest zamknięta, ale „Potrzebna wysyłka" bywa do poprawienia jeszcze
  // przed drukiem — dyrektor techniczny / prezes przełącza ją na zleceniu „Do wydruku".
  const canToggleShipping = isManager && order.status === "accepted";
  // Acceptor + timestamp live in the immutable event log (no dedicated field on the order).
  const acceptedEvent = order.events.find((e) => e.to_status === "accepted");

  return (
    <>
      <tr className="border-t border-[var(--color-border-muted)] align-top hover:bg-[var(--color-bg-elevated)] transition-colors">
        <td className="px-3 py-2.5 font-mono text-[var(--color-text-muted)]">#{order.number}</td>
        <td className="px-3 py-2.5">
          <div className="font-medium text-[var(--color-text-primary)]">{order.name}</div>
          <DescriptionBody html={order.description} />
        </td>
        <td className="px-3 py-2.5">
          {order.file ? <AttachmentLinks files={[order.file]} /> : <span className="text-[var(--color-text-muted)]">—</span>}
        </td>
        <td className="px-3 py-2.5">
          {order.needs_shipping
            ? <span className="text-[11px] font-mono text-teal-600">tak</span>
            : <span className="text-[11px] font-mono text-[var(--color-text-muted)]">nie</span>}
        </td>
        <td className="px-3 py-2.5">
          <StatusBadge order={order} />
          <TrackingNumberText value={order.tracking_number} />
        </td>
        <td className="px-3 py-2.5">
          <div className="text-[11px] text-[var(--color-text-secondary)]">{order.created_by_name || "—"}</div>
          <div className="text-[10px] font-mono text-[var(--color-text-muted)]">{fmtDate(order.created_at)}</div>
        </td>
        <td className="px-3 py-2.5">
          {acceptedEvent ? (
            <>
              <div className="text-[11px] text-[var(--color-text-secondary)]">{acceptedEvent.changed_by_name || "—"}</div>
              <div className="text-[10px] font-mono text-[var(--color-text-muted)]">{fmtDateTime(acceptedEvent.changed_at)}</div>
            </>
          ) : (
            <span className="text-[var(--color-text-muted)]">—</span>
          )}
        </td>
        <td className="px-3 py-2.5">
          <div className="flex flex-wrap items-start gap-1.5">
            {order.status === "pending" && canApprove && (
              <>
                <Button size="sm" variant="secondary" loading={m.accept.isPending} onClick={() => m.accept.mutate(order.id)}>
                  <Check size={13} />
                  Akceptuj
                </Button>
                <RejectButton order={order} />
              </>
            )}
            {order.status === "pending" && isAuthor && (
              <Button size="sm" variant="ghost" loading={m.cancel.isPending} onClick={() => m.cancel.mutate(order.id)}>
                <Ban size={13} />
                Anuluj
              </Button>
            )}
            {order.status === "accepted" && canRealize && (
              <Button size="sm" variant="secondary" loading={m.markPrinted.isPending} onClick={() => m.markPrinted.mutate(order.id)}>
                <Printer size={13} />
                Wydrukowane
              </Button>
            )}
            {order.status === "printed" && order.needs_shipping && canRealize && (
              <Button size="sm" variant="secondary" loading={m.markSent.isPending} onClick={() => m.markSent.mutate(order.id)}>
                <Send size={13} />
                Wysłane
              </Button>
            )}
            {canToggleShipping && (
              <Button
                size="sm"
                variant="ghost"
                loading={m.setShipping.isPending}
                title={order.needs_shipping
                  ? "Wyłącz wysyłkę — po wydruku zlecenie zostanie na miejscu (zmiana trafia do historii)."
                  : "Włącz wysyłkę — po wydruku zlecenie trafi do kolejki wysyłki (zmiana trafia do historii)."}
                onClick={() => m.setShipping.mutate({ id: order.id, needsShipping: !order.needs_shipping })}
              >
                <Truck size={13} />
                {order.needs_shipping ? "Bez wysyłki" : "Z wysyłką"}
              </Button>
            )}
            {canUndoPrint && (
              <Button
                size="sm"
                variant="ghost"
                loading={m.undoPrinted.isPending}
                title="Wróć do statusu „Do wydruku” — zlecenie znów trafia do kolejki działu realizacji."
                onClick={() => m.undoPrinted.mutate(order.id)}
              >
                <RotateCcw size={13} />
                Cofnij wydruk
              </Button>
            )}
            {order.status === "sent" && order.needs_shipping && (
              <>
                <ReceiptConfirmation order={order} canRealize={canRealize} />
                {canRealize && <MarkReturnedButton order={order} />}
              </>
            )}
            {order.status === "returned" && (
              <ReturnAttachment order={order} canRealize={canRealize} />
            )}
            {/* Nr listu — po oznaczeniu wysyłki; potwierdzenie nadania z poczty trafia na
                biurko później niż klik „Wysłane". */}
            {(order.status === "sent" || order.status === "returned") && order.needs_shipping && canRealize && (
              <PrintOrderTrackingNumber order={order} />
            )}
            {canEdit && (
              <Button size="sm" variant="ghost" onClick={() => setEditing((s) => !s)}>
                <Pencil size={13} />
                Edytuj
              </Button>
            )}
            {canDelete && <DeleteButton order={order} />}
            <Button size="sm" variant="ghost" onClick={() => setShowHistory((s) => !s)}>
              <History size={13} />
              Historia
            </Button>
          </div>
        </td>
      </tr>
      {editing && canEdit && (
        <tr className="border-t border-[var(--color-border-muted)] bg-[var(--color-bg-elevated)]">
          <td colSpan={8} className="px-3 py-2.5">
            <EditForm order={order} onDone={() => setEditing(false)} />
          </td>
        </tr>
      )}
      {showHistory && (
        <tr className="border-t border-[var(--color-border-muted)] bg-[var(--color-bg-elevated)]">
          <td colSpan={8} className="px-3 py-2.5">
            <div className="section-label mb-1.5">Historia</div>
            <ul className="space-y-1">
              {order.events.map((ev) => (
                <li key={ev.id} className="text-[11px] font-mono text-[var(--color-text-secondary)] flex flex-wrap gap-x-2">
                  <span className="text-[var(--color-text-muted)]">{fmtDateTime(ev.changed_at)}</span>
                  <span className="text-[var(--color-text-primary)]">{ev.changed_by_name}</span>
                  <span>{ev.from_status ? `${ev.from_status} → ${ev.to_status}` : ev.to_status}</span>
                  {ev.note && <span className="text-[var(--color-text-muted)]">· {ev.note}</span>}
                </li>
              ))}
            </ul>
          </td>
        </tr>
      )}
    </>
  );
}

// ── moje pisma do wysłania (dziennik korespondencji, wpisy wychodzące autora) ──
// Complements the print-order queue: a wniosek/list submitted in DLC_01 (kanał papierowy) lands
// in the DLC_02 correspondence register, where the dział obiegu sends it — but the author has no
// access to that register. This read-only table shows the author their own outgoing entries
// (backend `?authored=true`), so after submitting a wniosek they see it here as "do wysłania"
// and can follow it to "wysłane"/"doręczone" without founding a duplicate print order.
// Te same wpisy (ten sam `?authored=true`) widać w „Mojej korespondencji" na zakładce
// „Moje wysyłane" — tam jako pełne wiersze dziennika. Ta tabela zostaje przy zleceniach
// wydruku, bo tu właśnie autor stoi, gdy kusi go założenie dubla „na wszelki wypadek".
const ACTIVE_DISPATCH = new Set<CorrespondenceRegister["status"]>(["queued", "prepared"]);

function AuthoredDispatchRows({ rows, highlightId }: {
  rows: CorrespondenceRegister[];
  // Entry pointed at by a deep link from the source module (DLC_01 „otwórz w Moich pismach
  // do wysłania") — tinted and scrolled into view once on arrival.
  highlightId?: string | null;
}) {
  const scrolledTo = useRef<string | null>(null);
  // „Firmowy obieg dokumentów" — krąg, który pisma wysyła, więc i dopisuje nr listu (backend
  // re-enforces). Zwykły autor numeru nie nadaje: zobaczy go, gdy dział obiegu go wpisze.
  const canManageRegister = useAuthStore((s) => !!s.user?.has_correspondence_register_permission);
  // Link do projektu ZE jak w dzienniku: tylko z dostępem do DLC_01 i dla przedsięwzięcia
  // z projektem ZE — inne przedsięwzięcie zostaje samym tekstem.
  const canOpenProject = useAuthStore((s) => !!s.user?.accessible_dlc_modules?.includes("dlc_01"));
  return (
    <TableShell>
      <thead>
        <tr>
          <Th>Data</Th>
          <Th>Pismo</Th>
          <Th>Adresat</Th>
          <Th>Projekt</Th>
          <Th>Kanał</Th>
          <Th>Status</Th>
          <Th>Załączniki</Th>
          <Th>Akcje</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr
            key={r.id}
            ref={(el) => {
              if (el && r.id === highlightId && scrolledTo.current !== r.id) {
                scrolledTo.current = r.id;
                el.scrollIntoView({ block: "center" });
              }
            }}
            className={clsx(
              "border-t border-[var(--color-border-muted)] align-top hover:bg-[var(--color-bg-elevated)] transition-colors",
              r.id === highlightId && "bg-[var(--color-primary-dim)]",
            )}
          >
            <td className="px-3 py-2.5 font-mono text-[var(--color-text-muted)] whitespace-nowrap">
              {fmtDate(r.date ?? r.created_at)}
            </td>
            <td className="px-3 py-2.5">
              <div className="font-medium text-[var(--color-text-primary)]">{r.source_label || "—"}</div>
              {r.note && <div className="text-[11px] text-[var(--color-text-secondary)]">{r.note}</div>}
            </td>
            <td className="px-3 py-2.5">
              <div className="text-[11px] text-[var(--color-text-secondary)]">{r.counterparty_name}</div>
              {r.counterparty_address && (
                <div className="text-[10px] font-mono text-[var(--color-text-muted)]">{r.counterparty_address}</div>
              )}
            </td>
            <td className="px-3 py-2.5 text-[11px] text-[var(--color-text-secondary)]">
              {canOpenProject && r.target_project_has_ze && r.target_project && r.target_project_name ? (
                // New tab, as in the register — the author shouldn't lose their place in the list.
                <a
                  href={`/power-projects/${r.target_project}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 hover:text-[var(--color-primary)] hover:underline"
                  title="Otwórz projekt w nowej karcie"
                >
                  {r.target_project_name}
                  <ExternalLink className="h-3 w-3 shrink-0 text-[var(--color-text-muted)]" aria-hidden />
                </a>
              ) : (
                r.target_project_name || "—"
              )}
            </td>
            <td className="px-3 py-2.5 text-[11px] text-[var(--color-text-secondary)]">{r.channel_display}</td>
            <td className="px-3 py-2.5">
              <DispatchBadge status={r.status} />
              <TrackingNumberText value={r.tracking_number} />
            </td>
            <td className="px-3 py-2.5">
              {r.files.length > 0 || r.confirmation_file ? (
                <AttachmentLinks files={r.confirmation_file ? [...r.files, r.confirmation_file] : r.files} />
              ) : (
                <span className="text-[var(--color-text-muted)]">—</span>
              )}
            </td>
            <td className="px-3 py-2.5">
              {ACTIVE_DISPATCH.has(r.status) ? (
                <CancelDispatchButton row={r} />
              ) : canManageRegister && DISPATCHED_REGISTER_STATUSES.has(r.status) ? (
                // Autor pisma numeru nadania nie zna — wpisuje go dział obiegu; tu widzi go
                // tylko ten, kto sam to pismo złożył I ma uprawnienie do dziennika.
                <RegisterTrackingNumber row={r} />
              ) : (
                <span className="text-[var(--color-text-muted)]">—</span>
              )}
            </td>
          </tr>
        ))}
        {rows.length === 0 && (
          <tr>
            <td colSpan={8} className="px-3 py-10 text-center section-label tracking-wide">
              Brak pism w wysyłce. Wpis pojawia się tu automatycznie po złożeniu wniosku
              (kanał papierowy) w Projektach ZE.
            </td>
          </tr>
        )}
      </tbody>
    </TableShell>
  );
}

const STATUS_FILTERS: { value: PrintOrderStatus; label: string }[] = [
  { value: "pending", label: "Oczekuje na akceptację" },
  { value: "accepted", label: "Do wydruku" },
  { value: "printed", label: "Wydrukowane" },
  { value: "sent", label: "Wysłane" },
  { value: "returned", label: "Zwrot" },
  { value: "rejected", label: "Odrzucone" },
  { value: "cancelled", label: "Anulowane" },
];

type ApproverTab = "mine" | "to_accept" | "accepted" | "all";
type PageView = "orders" | "dispatch";

export function PrintOrdersPage() {
  const user = useAuthStore((s) => s.user);
  const myId = user?.id;
  const [adding, setAdding] = useState(false);
  // "Zlecenia wydruku" vs "Moje pisma do wysłania" (outgoing register entries I authored —
  // e.g. wnioski submitted in DLC_01 waiting in the dispatch queue of the dział obiegu).
  // Deep link from the source module: ?view=dispatch opens that tab, ?entry=<id> points at
  // the register entry to highlight (the "otwórz w Moich pismach…" link at the pismo).
  const [searchParams] = useSearchParams();
  const [view, setView] = useState<PageView>(
    searchParams.get("view") === "dispatch" ? "dispatch" : "orders",
  );
  const highlightId = searchParams.get("entry");

  const canApprove =
    !!user?.is_ceo ||
    (user?.role === "director" && !!user?.is_technical_director) ||
    user?.role === "coordinator";
  // DLC_02 access is the gate for print orders (see PrintOrderViewSet): every internal user here
  // may realize — mark printed/sent, attach the PoD. A contractor granted DLC_02 only submits and
  // tracks their own orders (the backend returns just those and refuses realization).
  const canRealize = user?.role !== "contractor";
  // Managers (CEO + technical directors) may edit any pending order; authors may edit their own
  // pending ones. Cofnięcie wydruku i usunięcie zlecenia ma autor NA RÓWNI z nimi (na własnym
  // zleceniu) — patrz canWithdraw w PrintOrderRow.
  const isManager = !!user?.is_ceo || (user?.role === "director" && !!user?.is_technical_director);

  // Approvers navigate by bucket (Moje / Do akceptacji / Zaakceptowane / Wszystkie); everyone
  // filters by status on top of that. The dział realizacji additionally filters by submitter.
  const [approverTab, setApproverTab] = useState<ApproverTab>("to_accept");
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [creatorFilter, setCreatorFilter] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const anyFilter = statusFilter.length > 0 || creatorFilter.length > 0 || search !== "";

  // "Do akceptacji" / "Zaakceptowane" ARE a status, so the status filter never combines with
  // them (it would only ever narrow to nothing): picking a status there moves to "Wszystkie",
  // picking one of those buckets clears the status filter.
  const isStatusBucket = (t: ApproverTab) => t === "to_accept" || t === "accepted";
  const changeStatusFilter = (values: string[]) => {
    setStatusFilter(values);
    if (values.length && canApprove && isStatusBucket(approverTab)) setApproverTab("all");
  };
  const changeApproverTab = (t: ApproverTab) => {
    setApproverTab(t);
    if (isStatusBucket(t)) setStatusFilter([]);
  };

  // The backend returns the full list to every internal DLC_02 user, so we fetch once and slice it
  // client-side (approvers navigate by bucket; everyone else filters by status / submitter).
  const { data, isLoading } = usePrintOrders();
  const allRows = useMemo(() => data ?? [], [data]);

  // Outgoing register entries authored by me (self-scoped on the backend). Sorted with the
  // ones still awaiting dispatch on top — that's what the tab counter points at.
  const { data: authoredData, isLoading: authoredLoading } = useCorrespondenceRegister({ authored: "true" });
  const authoredRows = useMemo(() => {
    const rows = [...(authoredData ?? [])];
    rows.sort((a, b) =>
      Number(ACTIVE_DISPATCH.has(b.status)) - Number(ACTIVE_DISPATCH.has(a.status))
      || b.created_at.localeCompare(a.created_at),
    );
    return rows;
  }, [authoredData]);
  const authoredPending = useMemo(
    () => authoredRows.filter((r) => ACTIVE_DISPATCH.has(r.status)).length,
    [authoredRows],
  );

  // Creator options for the realizacja filter, derived from the loaded orders (the dział
  // realizacji receives every order, so this lists every submitter present).
  const creatorOptions = useMemo(() => {
    const byId = new Map<string, string>();
    for (const o of allRows) {
      if (o.created_by) byId.set(o.created_by, o.created_by_name || o.created_by);
    }
    return Array.from(byId, ([value, label]) => ({ value, label })).sort((a, b) =>
      a.label.localeCompare(b.label, "pl"),
    );
  }, [allRows]);

  // Name + description per order, lower-cased for the search box. The description is rich-text
  // HTML, so search its text — not the markup (tag names, image URLs).
  const searchText = useMemo(() => {
    const parser = new DOMParser();
    return new Map(
      allRows.map((o) => [
        o.id,
        `${o.name} ${parser.parseFromString(o.description, "text/html").body.textContent ?? ""}`.toLocaleLowerCase("pl"),
      ]),
    );
  }, [allRows]);

  const tabCounts = useMemo(
    () => ({
      mine: allRows.filter((o) => o.created_by === myId).length,
      to_accept: allRows.filter((o) => o.status === "pending").length,
      accepted: allRows.filter((o) => o.status === "accepted").length,
      all: allRows.length,
    }),
    [allRows, myId],
  );

  const rows = useMemo(() => {
    let r = allRows;
    if (canApprove) {
      if (approverTab === "mine") r = r.filter((o) => o.created_by === myId);
      else if (approverTab === "to_accept") r = r.filter((o) => o.status === "pending");
      else if (approverTab === "accepted") r = r.filter((o) => o.status === "accepted");
    }
    if (statusFilter.length) r = r.filter((o) => statusFilter.includes(o.status));
    if (canRealize && creatorFilter.length) r = r.filter((o) => !!o.created_by && creatorFilter.includes(o.created_by));
    const q = search.trim().toLocaleLowerCase("pl");
    if (q) r = r.filter((o) => searchText.get(o.id)?.includes(q));
    return r;
  }, [allRows, canApprove, approverTab, myId, statusFilter, canRealize, creatorFilter, search, searchText]);

  return (
    <div className="p-3 md:p-4">
      <PageHeader
        title="Zlecenia wydruku"
        subtitle="Zlecenia wydruku dokumentów · DLC_02"
        actions={
          view === "orders" && (
            <Button size="sm" variant={adding ? "ghost" : "secondary"} onClick={() => setAdding((a) => !a)}>
              {adding ? "Anuluj" : "+ Nowe zlecenie"}
            </Button>
          )
        }
      />

      {/* No DLC_02 hub nav here: Dziennik / Moja korespondencja / Zlecenia wydruku / Urzędy
          all sit in the left sidebar already. */}
      {view === "orders" && (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <input
            placeholder="Szukaj po nazwie / opisie…"
            className="input-base sm:max-w-xs"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <FilterMultiSelect
            variant="field"
            label="Status"
            options={STATUS_FILTERS}
            selected={statusFilter}
            onChange={changeStatusFilter}
          />
          {canRealize && (
            <FilterMultiSelect
              variant="field"
              label="Zgłaszający"
              options={creatorOptions}
              selected={creatorFilter}
              onChange={setCreatorFilter}
              searchable
            />
          )}
          {anyFilter && (
            <Button
              size="sm"
              variant="danger"
              className="h-10 !text-[var(--color-primary)]"
              onClick={() => {
                setStatusFilter([]);
                setCreatorFilter([]);
                setSearch("");
              }}
            >
              Wyczyść filtry
            </Button>
          )}
        </div>
      )}

      <Tabs
        active={view}
        onChange={(k) => setView(k)}
        tabs={[
          { key: "orders", label: "Zlecenia wydruku" },
          { key: "dispatch", label: "Moje pisma do wysłania", count: authoredPending, countTone: "warn" },
        ]}
      />

      {view === "dispatch" ? (
        authoredLoading ? (
          <div className="py-10 flex justify-center"><Spinner /></div>
        ) : (
          <AuthoredDispatchRows rows={authoredRows} highlightId={highlightId} />
        )
      ) : (
        <>
          {canApprove && (
            <Tabs
              active={approverTab}
              onChange={changeApproverTab}
              tabs={[
                { key: "mine", label: "Moje", count: tabCounts.mine, countTone: "blue" },
                { key: "to_accept", label: "Do akceptacji", count: tabCounts.to_accept, countTone: "warn" },
                { key: "accepted", label: "Zaakceptowane", count: tabCounts.accepted, countTone: "blue" },
                { key: "all", label: "Wszystkie", count: tabCounts.all, countTone: "blue" },
              ]}
            />
          )}

          {adding && <CreateForm onDone={() => setAdding(false)} />}

          {isLoading ? (
            <div className="py-10 flex justify-center"><Spinner /></div>
          ) : (
            <TableShell>
              <thead>
                <tr>
                  <Th>LP</Th>
                  <Th>Nazwa / opis</Th>
                  <Th>Plik</Th>
                  <Th>Wysyłka</Th>
                  <Th>Status</Th>
                  <Th>Zgłaszający</Th>
                  <Th>Zaakceptował</Th>
                  <Th>Akcje</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((order) => (
                  <PrintOrderRow key={order.id} order={order} canApprove={canApprove} canRealize={canRealize} isManager={isManager} myId={user?.id} />
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-3 py-10 text-center section-label tracking-wide">
                      Brak zleceń wydruku.
                    </td>
                  </tr>
                )}
              </tbody>
            </TableShell>
          )}
        </>
      )}
    </div>
  );
}
